/* ============================================
   LeadDetailView — ficha de la oportunidad

   Shell de la vista: ruta, identidad, etapa y
   pestañas a la izquierda; contactos y empresa a la
   derecha (`LeadSidebar`, donde el chat se abre a
   demanda por contacto).

   Cada pestaña es su propio componente y se trae lo
   suyo. Acá solo queda lo que comparten: el lead, la
   etapa y las reuniones — que la agenda gestiona pero
   la actividad del Overview también necesita.
   ============================================ */
import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import type { Lead, EtapaPipeline, Servicio } from '@/lib/types'

import { PageHeader } from '@/components/ui'
import LeadSidebar from './LeadSidebar'
import LeadOverviewTab from './LeadOverviewTab'
import LeadTareasTab from './LeadTareasTab'
import LeadReunionesTab, { type Agendamiento } from './LeadReunionesTab'
import LeadMaterialTab from './LeadMaterialTab'
import CierreLeadModal, { mensajeDeCierre } from './CierreLeadModal'
import { showToast } from '@/components/layout/Toast'
import PanelCorreos from '@/components/correos/PanelCorreos'
import {
  IconBuilding, IconFileText, IconEdit, IconArrowRight, IconCheck, IconXCircle,
} from '@/components/icons/Icons'

const ETAPAS: { key: EtapaPipeline; label: string }[] = [
  // `prospecto` solo se muestra mientras el lead está ahí: es outbound que
  // todavía no se contactó y no pertenece al tablero.
  { key: 'prospecto', label: 'Prospecto' },
  { key: 'lead', label: 'Lead' },
  { key: 'contactado', label: 'Contactado' },
  { key: 'diagnostico', label: 'Diagnóstico' },
  { key: 'cotizado', label: 'Cotizado' },
  { key: 'negociacion', label: 'Negociación' },
  { key: 'cerrado', label: 'Cerrado' },
]

/*
  Ya no hay 'notas': el texto libre de la oportunidad es el LIENZO, arriba del
  Overview (`LeadLienzo`). Es el mismo campo `leads.notas`, movido a donde se
  lo mira — y uno solo, para que no haya dos lugares donde buscar lo mismo.
*/
type Pestana = 'overview' | 'material' | 'correos' | 'tareas' | 'reuniones'

interface LeadDetailViewProps {
  lead: Lead
  onClose: () => void
  onRefresh: () => void
  /** De dónde se entró: la ficha se abre desde el pipeline y desde prospección. */
  origen?: string
}

export default function LeadDetailView({ lead, onClose, onRefresh, origen = 'Pipeline' }: LeadDetailViewProps) {
  const api = useApi()
  /*
    Dos pestañas no son del partner:

    · Correos abre el buzón de Gmail de 1Conecta — el token de Google es de la
      empresa, por eso TODAS esas rutas son adminOnly (`correos-archivados.ts`).
      En su panel la solapa igual se dibujaba y respondía 403.
    · Reuniones queda fuera por ahora, a pedido. El historial del Overview sí
      sigue nombrándolas: ahí es una fecha, no la grabación ni el Drive.
  */
  const enPartner = useLocation().pathname.startsWith('/partner')
  const [pestana, setPestana] = useState<Pestana>('overview')
  const [etapa, setEtapa] = useState<EtapaPipeline>(lead.etapa)
  /* Mover a Cerrado abre la pregunta, no guarda: ver `CierreLeadModal`. */
  const [cerrando, setCerrando] = useState(false)
  const [guardando, setGuardando] = useState(false)

  const [editandoTitulo, setEditandoTitulo] = useState(false)
  const [tituloDraft, setTituloDraft] = useState(lead.titulo || '')
  const [editandoEmpresa, setEditandoEmpresa] = useState(false)
  const [empresaDraft, setEmpresaDraft] = useState(lead.empresa || '')

  const [meetings, setMeetings] = useState<Agendamiento[]>([])
  const [cargandoMeetings, setCargandoMeetings] = useState(false)
  const [tareasPendientes, setTareasPendientes] = useState(0)
  // El contador de correos se pide acá para que la solapa lo muestre sin
  // haberla abierto; después lo mantiene al día el propio panel.
  const [correosCount, setCorreosCount] = useState(0)

  /*
    Pedirnos la cotización. Es el camino que ya ocurría por WhatsApp, puesto
    donde queda registrado: el partner junta el contexto en la oportunidad —el
    lienzo, los adjuntos, las reuniones— y pide que el documento lo armemos
    nosotros en el ERP.

    No se crea una cotización en borrador al pedirla: una fila que nadie abrió
    ensucia el listado de los dos lados. Lo que queda es la señal de que falta
    hacerla.
  */
  const [pidiendo, setPidiendo] = useState(false)
  const [notaPedido, setNotaPedido] = useState('')
  const [enviandoPedido, setEnviandoPedido] = useState(false)
  const [errorPedido, setErrorPedido] = useState<string | null>(null)
  const [pedidaAt, setPedidaAt] = useState<string | null>(lead.cotizacion_solicitada_at ?? null)

  useEffect(() => {
    setPedidaAt(lead.cotizacion_solicitada_at ?? null)
  }, [lead.id, lead.cotizacion_solicitada_at])

  async function pedirCotizacion() {
    setEnviandoPedido(true)
    setErrorPedido(null)
    try {
      const r = await api.post<{ cotizacion_solicitada_at: string }>(
        `/api/leads/${lead.id}/solicitar-cotizacion`,
        { nota: notaPedido },
      )
      setPedidaAt(r.cotizacion_solicitada_at)
      setPidiendo(false)
      setNotaPedido('')
      onRefresh()
    } catch (err: any) {
      console.error('Error pidiendo la cotización:', err)
      setErrorPedido(err?.message || 'No se pudo enviar el pedido')
    } finally {
      setEnviandoPedido(false)
    }
  }

  // El catálogo se pide una vez por ficha y lo comparten el selector del
  // Overview y la pestaña Material: `GET /api/servicios` ya trae `recursos[]`,
  // así que el material no cuesta una consulta aparte.
  const [servicios, setServicios] = useState<Servicio[]>([])
  const [cargandoServicios, setCargandoServicios] = useState(true)

  const apiGet = api.get

  useEffect(() => {
    setEtapa(lead.etapa)
    setTituloDraft(lead.titulo || '')
    setEmpresaDraft(lead.empresa || '')
  }, [lead.id, lead.etapa, lead.titulo, lead.empresa])

  const cargarMeetings = useCallback(async () => {
    setCargandoMeetings(true)
    try {
      setMeetings(await apiGet<Agendamiento[]>(`/api/booking/agendamientos/by-lead/${lead.id}`))
    } catch (err) {
      console.error('Error cargando reuniones:', err)
      setMeetings([])
    } finally {
      setCargandoMeetings(false)
    }
  }, [apiGet, lead.id])

  useEffect(() => { cargarMeetings() }, [cargarMeetings])

  useEffect(() => {
    if (enPartner) { setCorreosCount(0); return }
    let cancelado = false
    apiGet<unknown[]>(`/api/leads/${lead.id}/correos`)
      .then(r => { if (!cancelado) setCorreosCount(Array.isArray(r) ? r.length : 0) })
      .catch(() => { if (!cancelado) setCorreosCount(0) })
    return () => { cancelado = true }
  }, [apiGet, lead.id, enPartner])

  useEffect(() => {
    let cancelado = false
    setCargandoServicios(true)
    apiGet<Servicio[]>('/api/servicios')
      .then(r => { if (!cancelado) setServicios(Array.isArray(r) ? r : []) })
      .catch(err => { console.error('Error cargando servicios:', err); if (!cancelado) setServicios([]) })
      .finally(() => { if (!cancelado) setCargandoServicios(false) })
    return () => { cancelado = true }
  }, [apiGet])

  const servicioDelLead = lead.servicio_id
    ? servicios.find(s => s.id === lead.servicio_id) || null
    : null
  // Material enviable al cliente: lo interno no se cuenta en la solapa.
  const materialPublico = (servicioDelLead?.recursos || []).filter(r => r.publico).length

  const onTareasCount = useCallback((_total: number, pendientes: number) => {
    setTareasPendientes(pendientes)
  }, [])

  // ─── Identidad ───

  // El encabezado nombra la oportunidad, no a su contacto: el contacto puede
  // cambiar (o ser varios) y la oportunidad sigue siendo la misma. Sin titulo
  // manda la empresa, y solo si tampoco la hay, el codigo.
  const nombreVisible = lead.titulo || lead.empresa || lead.codigo
  const fuenteIniciales = lead.empresa || lead.titulo || lead.codigo
  // Dos letras siempre: con varias palabras, la inicial de cada una; con una
  // sola (el codigo, sin titulo todavia), sus dos primeros caracteres.
  const palabras = fuenteIniciales.split(' ').filter(Boolean)
  const iniciales = (palabras.length > 1
    ? palabras.map(w => w[0]).slice(0, 2).join('')
    : fuenteIniciales.slice(0, 2)
  ).toUpperCase()

  // Una vez promovido, el paso "Prospecto" desaparece: no se vuelve a una
  // lista de la que ya salió.
  const pasos = etapa === 'prospecto' ? ETAPAS : ETAPAS.filter(e => e.key !== 'prospecto')
  const indiceEtapa = pasos.findIndex(e => e.key === etapa)
  const siguienteEtapa = indiceEtapa >= 0 && indiceEtapa < pasos.length - 1 ? pasos[indiceEtapa + 1] : null
  const etapaLabel = ETAPAS.find(e => e.key === etapa)?.label || etapa

  async function cambiarEtapa(nueva: EtapaPipeline) {
    if (nueva === etapa) return
    /*
      Cerrar es la unica etapa que pregunta algo: si se gano o se perdio. Sin
      eso, el lead cerrado no dice cual de las dos cosas pasó — y si se gano,
      es el momento en que lo que el partner nos debe se vuelve factura.
    */
    if (nueva === 'cerrado') { setCerrando(true); return }
    setGuardando(true)
    try {
      await api.put(`/api/leads/${lead.id}`, { etapa: nueva })
      setEtapa(nueva)
      onRefresh()
    } catch (err) { console.error('Error cambiando etapa:', err) }
    finally { setGuardando(false) }
  }

  async function guardarTitulo() {
    if (tituloDraft === (lead.titulo || '')) { setEditandoTitulo(false); return }
    try {
      await api.put(`/api/leads/${lead.id}`, { titulo: tituloDraft.trim() || null })
      setEditandoTitulo(false)
      onRefresh()
    } catch (err) { console.error('Error guardando título:', err) }
  }

  async function guardarEmpresa() {
    if (empresaDraft === (lead.empresa || '')) { setEditandoEmpresa(false); return }
    try {
      await api.put(`/api/leads/${lead.id}`, { empresa: empresaDraft.trim() || null })
      setEditandoEmpresa(false)
      onRefresh()
    } catch (err) { console.error('Error guardando empresa:', err) }
  }

  return (
    <div className="ld-view">
      <PageHeader
        breadcrumbs={[
          { label: 'CRM' },
          { label: origen, onClick: onClose },
          { label: lead.codigo },
        ]}
        meta={lead.es_agencia ? <span className="crm-lead-badge crm-lead-badge--agency">Agencia</span> : undefined}
      />

      <div className="ld-content">
        {/* ─── Columna principal ─── */}
        <div className="ld-main">
          <header className="ld-identity">
            <div className="ld-avatar">{iniciales || '?'}</div>

            <div className="ld-identity__body">
              {editandoTitulo ? (
                <input
                  className="form-input ld-name-input"
                  value={tituloDraft}
                  onChange={e => setTituloDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') guardarTitulo() }}
                  onBlur={guardarTitulo}
                  placeholder="Nombre de la oportunidad"
                  autoFocus
                />
              ) : (
                <h2 className="ld-contact-name" onClick={() => { setTituloDraft(lead.titulo || ''); setEditandoTitulo(true) }}>
                  {nombreVisible} <IconEdit size={12} />
                </h2>
              )}

              {editandoEmpresa ? (
                <input
                  className="form-input form-input--inline-mini ld-empresa-input"
                  value={empresaDraft}
                  onChange={e => setEmpresaDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') guardarEmpresa() }}
                  onBlur={guardarEmpresa}
                  placeholder="Empresa..."
                  autoFocus
                />
              ) : (
                <div className="ld-empresa" onClick={() => { setEmpresaDraft(lead.empresa || ''); setEditandoEmpresa(true) }}>
                  <IconBuilding size={13} />
                  {lead.empresa || <span className="text-muted-italic">+ Agregar empresa</span>}
                </div>
              )}
            </div>

            <div className="ld-identity__actions">
              {pedidaAt ? (
                /* Pedida: lo que falta es que la armemos nosotros, así que el
                   botón deja de ser una acción y pasa a ser un estado. */
                <span className="ld-cotizacion-pedida" title={`Pedida el ${new Date(pedidaAt).toLocaleString('es-PE')}`}>
                  <IconCheck size={13} /> Cotización pedida
                </span>
              ) : (
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={() => setPidiendo(true)}
                >
                  <IconFileText size={14} /> Cotizar
                </button>
              )}
            </div>
          </header>

          {/* ─── Etapa ─── */}
          <div className="ld-etapa">
            <div className="ld-stage-selector">
              {pasos.map((e, i) => (
                <button
                  key={e.key}
                  type="button"
                  className={`ld-stage-pill${e.key === etapa ? ' ld-stage-pill--active' : ''}${i < indiceEtapa ? ' ld-stage-pill--done' : ''}`}
                  onClick={() => cambiarEtapa(e.key)}
                  disabled={guardando}
                >
                  {e.label}
                </button>
              ))}
            </div>
            {/* Cerrada: en lugar del boton de avanzar, como termino. Un lead
                cerrado sin esto se lee igual que uno ganado y uno perdido. */}
            {etapa === 'cerrado' && lead.resultado ? (
              <span className={`ld-resultado ld-resultado--${lead.resultado}`} title={lead.motivo_perdida || undefined}>
                {lead.resultado === 'ganado' ? <IconCheck size={13} /> : <IconXCircle size={13} />}
                {lead.resultado === 'ganado' ? 'Ganado' : 'Perdido'}
              </span>
            ) : siguienteEtapa && (
              <button
                type="button"
                className="btn btn-secondary btn-sm ld-etapa__siguiente"
                onClick={() => cambiarEtapa(siguienteEtapa.key)}
                disabled={guardando}
              >
                Mover a {siguienteEtapa.label} <IconArrowRight size={13} />
              </button>
            )}
          </div>

          {/* ─── Pestañas ─── */}
          <div className="ld-tabs">
            <button type="button" className={`ld-tab${pestana === 'overview' ? ' ld-tab--active' : ''}`} onClick={() => setPestana('overview')}>
              Overview
            </button>
            <button type="button" className={`ld-tab${pestana === 'material' ? ' ld-tab--active' : ''}`} onClick={() => setPestana('material')}>
              Material {materialPublico > 0 && <span className="ld-tab-count">{materialPublico}</span>}
            </button>
            {!enPartner && (
              <button type="button" className={`ld-tab${pestana === 'correos' ? ' ld-tab--active' : ''}`} onClick={() => setPestana('correos')}>
                Correos {correosCount > 0 && <span className="ld-tab-count">{correosCount}</span>}
              </button>
            )}
            <button type="button" className={`ld-tab${pestana === 'tareas' ? ' ld-tab--active' : ''}`} onClick={() => setPestana('tareas')}>
              Tareas {tareasPendientes > 0 && <span className="ld-tab-count">{tareasPendientes}</span>}
            </button>
            {!enPartner && (
              <button type="button" className={`ld-tab${pestana === 'reuniones' ? ' ld-tab--active' : ''}`} onClick={() => setPestana('reuniones')}>
                Reuniones {meetings.length > 0 && <span className="ld-tab-count">{meetings.length}</span>}
              </button>
            )}
          </div>

          <div className="ld-tab-content">
            {pestana === 'overview' && (
              <LeadOverviewTab lead={lead} servicios={servicios} onRefresh={onRefresh} />
            )}
            {pestana === 'material' && (
              <LeadMaterialTab lead={lead} servicio={servicioDelLead} cargandoCatalogo={cargandoServicios} />
            )}
            {pestana === 'correos' && !enPartner && (
              <PanelCorreos
                basePath={`/api/leads/${lead.id}/correos`}
                nombreFicha="oportunidad"
                onCount={setCorreosCount}
              />
            )}
            {pestana === 'tareas' && (
              <LeadTareasTab leadId={lead.id} onCountChange={onTareasCount} />
            )}
            {pestana === 'reuniones' && !enPartner && (
              <LeadReunionesTab leadId={lead.id} meetings={meetings} loading={cargandoMeetings} onReload={cargarMeetings} />
            )}
          </div>
        </div>

        {/* ─── Pedido de cotización ─── */}
        {pidiendo && (
          <div className="ld-modal-fondo" role="dialog" aria-modal="true" aria-label="Pedir la cotización">
            <div className="ld-modal">
              <h3 className="ld-modal__titulo">Pedirle la cotización a 1Conecta</h3>
              <p className="ld-modal__texto">
                El equipo arma el documento con tu marca y te avisa. Ya van con el pedido el
                lienzo de esta oportunidad, sus adjuntos y sus reuniones — agrega abajo lo que
                no esté escrito ahí.
              </p>
              <textarea
                className="form-input ld-modal__nota"
                value={notaPedido}
                onChange={e => setNotaPedido(e.target.value)}
                placeholder="Qué pidió el cliente, con qué presupuesto, para cuándo lo necesita..."
                rows={5}
                autoFocus
              />
              {errorPedido && <p className="ld-tareas__error">{errorPedido}</p>}
              <div className="ld-modal__acciones">
                <button type="button" className="btn btn-secondary" onClick={() => setPidiendo(false)} disabled={enviandoPedido}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={pedirCotizacion} disabled={enviandoPedido}>
                  {enviandoPedido ? 'Enviando...' : 'Pedir la cotización'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Cierre: ganado o perdido ─── */}
        {cerrando && (
          <CierreLeadModal
            lead={lead}
            onCancelar={() => setCerrando(false)}
            onCerrado={res => {
              setCerrando(false)
              setEtapa('cerrado')
              showToast(mensajeDeCierre(res), 'success')
              for (const aviso of res.avisos) showToast(aviso, 'warning')
              onRefresh()
            }}
          />
        )}

        {/* ─── Barra lateral ─── */}
        <LeadSidebar lead={lead} onRefresh={onRefresh} meetings={meetings} etapaLabel={etapaLabel} />
      </div>
    </div>
  )
}
